import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type {
  ApAutomatedRunItem,
  ApEmailResponse,
  ApInvoice,
  ApPage,
  ApReceipt,
  ApReviewQueueFilters,
  ApReviewQueueItem,
  ApReviewQueueVendor,
  ApVendor,
} from "@/accounts-payable/types";
import { enrichApEmail } from "@/accounts-payable/utils";
import { useApi } from "@/core/api";
import {
  automatedInvoicesKey,
  emailsKey,
  invoicesKey,
  receiptsKey,
  reviewQueueKey,
  reviewQueueVendorsKey,
  vendorsKey,
} from "./keys";
import {
  type ApInvoiceListFilters,
  dayParams,
  invoiceListParams,
  normalizeInvoiceFilters,
  ownerAndActionParams,
  sortedOrNull,
} from "./list-params";
import {
  AP_STALE_TIME,
  apInvoiceListRefetchInterval,
  apListRefetchInterval,
} from "./polling";

export type { ApInvoiceListFilters } from "./list-params";

export const useGetInvoices = (
  businessId?: string | null,
  filters: ApInvoiceListFilters = {},
  options?: { enabled?: boolean },
) => {
  const api = useApi();
  const normalized = normalizeInvoiceFilters(filters);
  return useInfiniteQuery({
    queryKey: [...invoicesKey(businessId), "list", normalized] as const,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApInvoice>>(`/businesses/${businessId}/ap/invoices`, {
          params: { page: pageParam, ...invoiceListParams(normalized) },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId && (options?.enabled ?? true),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

export type ApReceiptListFilters = Omit<ApInvoiceListFilters, "inQuickbooks">;

export const useGetReceipts = (
  businessId?: string | null,
  filters: ApReceiptListFilters = {},
  options?: { enabled?: boolean },
) => {
  const api = useApi();
  const normalized = {
    statuses: filters.statuses?.length ? [...filters.statuses].sort() : null,
    search: filters.search?.trim() || null,
    added: filters.added ?? null,
    reviewed: filters.reviewed ?? null,
    reviewStates: filters.reviewStates?.length
      ? [...filters.reviewStates].sort()
      : null,
  };
  return useInfiniteQuery({
    queryKey: [...receiptsKey(businessId), "list", normalized] as const,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApReceipt>>(`/businesses/${businessId}/ap/receipts`, {
          params: {
            page: pageParam,
            ...(normalized.statuses && {
              "filter[status]": normalized.statuses,
            }),
            ...(normalized.search && { q: normalized.search }),
            ...dayParams("created", normalized.added),
            ...dayParams("reviewed", normalized.reviewed),
            ...(normalized.reviewStates && {
              "filter[review_state]": normalized.reviewStates,
            }),
          },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId && (options?.enabled ?? true),
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

export const useGetEmails = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: emailsKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApEmailResponse>>(`/businesses/${businessId}/ap/emails`, {
          params: { page: pageParam },
        })
        .then((response) => ({
          ...response.data,
          items: response.data.items.map(enrichApEmail),
        })),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};

export const useGetVendors = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: vendorsKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApVendor>>(`/businesses/${businessId}/ap/vendors`, {
          params: { page: pageParam },
        })
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};

export const useGetReviewQueue = (
  businessId?: string | null,
  refreshOnMount = false,
  filters?: ApReviewQueueFilters,
) => {
  const api = useApi();
  const normalized = {
    actionTypes: sortedOrNull(filters?.actionTypes),
    assigneeIds: sortedOrNull(filters?.assigneeIds),
    vendorIds: sortedOrNull(filters?.vendorIds),
    search: filters?.search?.trim() || null,
  };
  const filtered = Object.values(normalized).some((value) => value !== null);
  return useInfiniteQuery({
    queryKey: filtered
      ? ([...reviewQueueKey(businessId), normalized] as const)
      : reviewQueueKey(businessId),
    refetchOnMount: refreshOnMount ? "always" : true,
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApReviewQueueItem>>(
          `/businesses/${businessId}/ap/invoices/review_queue`,
          {
            params: {
              page: pageParam,
              ...ownerAndActionParams(normalized),
              ...(normalized.vendorIds && {
                "filter[vendor_id]": normalized.vendorIds,
              }),
              ...(normalized.search && { q: normalized.search }),
            },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    // A new search or filter keeps the previous result up until its own
    // arrives, rather than blanking the list on every pause in typing.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    refetchInterval: apInvoiceListRefetchInterval,
  });
};

/**
 * The Review view's Vendor filter choices: the queue's vendors, plus any
 * already chosen, so a choice whose last invoice was reviewed keeps its name.
 */
export const useGetReviewQueueVendors = (
  businessId: string | null,
  selectedIds: string[],
) => {
  const api = useApi();
  const selected = sortedOrNull(selectedIds);
  return useQuery({
    queryKey: [...reviewQueueVendorsKey(businessId), selected] as const,
    queryFn: () =>
      api
        .get<
          ApReviewQueueVendor[]
        >(`/businesses/${businessId}/ap/invoices/review_queue_vendors`, { params: selected ? { "filter[vendor_id]": selected } : {} })
        .then((response) => response.data),
    enabled: !!businessId,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[1] === businessId ? previousData : undefined,
    staleTime: AP_STALE_TIME,
    // Polled like the queue, so a vendor whose first invoice arrives while
    // Review is open becomes a choice.
    refetchInterval: AP_STALE_TIME,
  });
};

export const useGetAutomatedInvoices = (businessId?: string | null) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: automatedInvoicesKey(businessId),
    queryFn: ({ pageParam }) =>
      api
        .get<ApPage<ApAutomatedRunItem>>(
          `/businesses/${businessId}/ap/invoices/automated`,
          {
            params: { page: pageParam },
          },
        )
        .then((response) => response.data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : undefined,
    enabled: !!businessId,
    staleTime: AP_STALE_TIME,
    refetchInterval: apListRefetchInterval,
  });
};
